import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Waves, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ImageSlot } from "@/components/ImageSlot";
import { BoatCard } from "@/components/public/BoatCard";
import { LeadDialog } from "@/components/public/LeadDialog";
import { Seo } from "@/components/Seo";
import { api } from "@/lib/api";

export default function LocationPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [boats, setBoats] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPage(null); setNotFound(false);
    api.get(`/location-pages/${slug}`).then((r) => {
      setPage(r.data);
      const bq = r.data.brand_emphasis ? `?brand=${encodeURIComponent(r.data.brand_emphasis)}` : "";
      api.get(`/boats${bq}`).then((b) => setBoats((b.data || []).filter((x) => x.status !== "sold").slice(0, 6))).catch(() => {});
    }).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <div className="max-w-3xl mx-auto px-4 py-24 text-center"><h1 className="text-3xl font-bold font-heading">Page not found</h1><Button asChild className="mt-6 bg-mtm-purple text-white rounded-full"><Link to="/">Home</Link></Button></div>;
  if (!page) return <div className="max-w-7xl mx-auto px-4 py-24 text-center text-slate-500">Loading...</div>;

  return (
    <div data-testid={`location-page-${slug}`}>
      <Seo title={page.meta_title} description={page.meta_description} />
      <section className="relative bg-mtm-charcoal text-white overflow-hidden">
        <div className="absolute inset-0 opacity-25"><ImageSlot label={page.image_slot} dimensions="" aspect="h-full" className="h-full rounded-none border-0 bg-slate-800" /></div>
        <div className="absolute inset-0 bg-gradient-to-r from-mtm-purple-deep/95 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 py-20">
          <p className="text-xs font-bold uppercase tracking-widest text-mtm-teal-bright font-mono flex items-center gap-1"><MapPin size={13} /> {page.city}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold font-heading tracking-tight mt-3 max-w-3xl">{page.h1}</h1>
          <p className="text-lg text-slate-200 mt-4 max-w-2xl">{page.intro}</p>
          <div className="flex flex-wrap gap-3 mt-7">
            <Button asChild size="lg" className="bg-mtm-teal hover:bg-mtm-teal-bright text-white rounded-full px-7"><Link to="/inventory">Shop Inventory</Link></Button>
            <LeadDialog ctaType={`${page.city} Inquiry`} triggerLabel="Contact MTM" variant="outline" size="lg" />
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 py-14">
        {(page.lakes || []).length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8">
            {page.lakes.map((l) => <span key={l} className="flex items-center gap-1 text-sm bg-mtm-teal/10 text-mtm-teal-dark px-3 py-1 rounded-full font-medium"><Waves size={14} /> {l}</span>)}
          </div>
        )}
        <div className="space-y-8">
          {(page.sections || []).map((sec, i) => (
            <div key={i}>
              <h2 className="font-heading font-bold text-xl sm:text-2xl text-slate-900">{sec.heading}</h2>
              <p className="text-slate-600 mt-2 leading-relaxed">{sec.body}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 flex flex-wrap gap-3 text-sm">
          <Link to="/brands/falcon" className="text-mtm-purple font-semibold hover:underline">Falcon Boats</Link>
          <span className="text-slate-300">·</span>
          <Link to="/brands/armada" className="text-mtm-purple font-semibold hover:underline">Armada Pontoons</Link>
          <span className="text-slate-300">·</span>
          <Link to="/inventory?condition=used" className="text-mtm-purple font-semibold hover:underline">Used Inventory</Link>
          <span className="text-slate-300">·</span>
          <Link to="/service" className="text-mtm-purple font-semibold hover:underline">Service</Link>
          <span className="text-slate-300">·</span>
          <Link to="/contact" className="text-mtm-purple font-semibold hover:underline">Contact</Link>
        </div>
      </section>

      {boats.length > 0 && (
        <section className="bg-white border-t border-slate-200 py-14">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-end justify-between mb-8">
              <h2 className="font-heading font-bold text-2xl sm:text-3xl text-slate-900">Available Inventory</h2>
              <Link to="/inventory" className="hidden sm:flex items-center gap-1 text-sm font-semibold text-mtm-purple hover:gap-2 transition-all">View All <ArrowRight size={15} /></Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">{boats.map((b, i) => <BoatCard key={b.id} boat={b} index={i} />)}</div>
          </div>
        </section>
      )}

      <section className="bg-gradient-to-r from-mtm-purple to-mtm-teal-dark py-12">
        <div className="max-w-7xl mx-auto px-4 text-center text-white">
          <p className="text-lg max-w-2xl mx-auto">{page.cta}</p>
          <div className="mt-5 flex justify-center gap-3">
            <Button asChild className="bg-white text-mtm-purple hover:bg-slate-100 rounded-full px-7"><Link to="/inventory">Browse Boats</Link></Button>
            <LeadDialog ctaType={`${page.city} Inquiry`} triggerLabel="Get in Touch" variant="teal" />
          </div>
        </div>
      </section>
    </div>
  );
}
